// Grille mensuelle — month grid + payroll review redesign (soft island
// .gmapp, port of design_handoff_grille_mensuelle). Three views — grid /
// review / submitted — driven by the URL (?view=review) + server state
// instead of the proto's localStorage machine.
//
// Core rule (locked): décisions touch the PAIE version only — réelle
// stays the raw clock truth. Paie caps every worked day at the expected
// hours automatically; the decide queue is short days (court) + data
// anomalies (absj, double) only, and every menu option classifies the
// day at full expected hours (nothing computes money).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, Download, Zap } from 'lucide-react';
import {
  GM_KIND_MENUS,
  gmMenuFor,
  type GmCustomType,
  type GmDecisionOption,
  type GmNeedKind,
} from '@pointage/shared';
import { useUiStore } from '../store/ui.js';
import { useUnsavedGuard } from '../lib/unsaved.js';
import {
  useMe,
  useMonthlyGrid,
  useSubmitMonthly,
  usePaieDecision,
  usePutAttendance,
  useDecisionTypes,
  type MonthCellDTO,
  type MonthCellType,
  type MonthGridDTO,
  type MonthGridRowDTO,
  type MonthlyStage,
} from '../api/hooks.js';
import { toast } from '../store/toast.js';
import { formatHoursToHMM } from '../lib/format.js';
import '../styles/grille-mensuelle.css';

const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];
const DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const DOW_NAME = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
type Version = 'real' | 'paie';

// "8h06" (always-padded minutes) — same as the proto's fr().
const fr = formatHoursToHMM;
// "168h" / "168h30" — totals drop ":00".
function frTotal(h: number): string {
  const totalMin = Math.round(h * 60);
  const H = Math.floor(totalMin / 60);
  const M = totalMin % 60;
  return M ? `${H}h${String(M).padStart(2, '0')}` : `${H}h`;
}
function gmInitials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] ?? '').join('').toUpperCase();
}

// Cell tone classes (t-*) — the proto's calm encoding. 'p' splits into
// pres/over client-side (over = réelle hours beyond the expected cap).
const TONE: Record<MonthCellType, string> = {
  p: 'pres', r: 'late', recup: 'recup', tt: 'tt', c: 'conge', m: 'maladie',
  aj: 'justif', anj: 'abs', h: 'ferie', we: 'off', empty: 'off', need: 'decide',
  na: 'na',
};
const CODE: Partial<Record<MonthCellType, string>> = {
  recup: 'R', tt: 'TT', c: 'C', m: 'M', aj: 'AJ', anj: 'A', h: 'F',
  // « pointage non importé » — un tiret neutre, jamais le rouge d'une absence.
  na: '–',
};

interface CellView {
  tone: string;
  text: string;
  decide?: boolean;
  resolved?: boolean;
  /** Unresolved queue reason — tints the « ? » cell (absj rouge,
   * court orange, double violet). */
  kind?: string;
  /** Réelle only: the queued cell is already arbitrated. The cell shows
   * the raw punch reading and `tag` names the paie decision. */
  decided?: boolean;
  tag?: { text: string; tone: string };
}

/** Corner tag naming a paie decision in Version réelle — the code of
 *  the resulting cell type, or ✓ when the day counts as worked. */
function decisionTag(result: MonthCellType | 'reel'): { text: string; tone: string } {
  if (result === 'reel' || result === 'p' || result === 'r') return { text: '✓', tone: 'pres' };
  return { text: CODE[result] ?? '✓', tone: TONE[result] ?? 'pres' };
}

interface QueueItem {
  employeeId: string;
  name: string;
  role: string | null;
  date: string;
  day: number;
  dayIdx: number;
  kind: GmNeedKind;
  cell: MonthCellDTO;
}

// Local echo of just-committed decisions so the review flow is instant
// while the grid refetches (the server response is authoritative).
type Overlay = Record<string, GmDecisionOption | null>;
const ovKey = (employeeId: string, date: string) => `${employeeId}__${date}`;

/** What resolved a queue cell — local echo first, then server state.
 *  `result` porte le résultat d'arbitrage: un type de cellule, ou
 *  « reel » (compter les heures pointées) qui n'en est pas un. */
type Resolution = { key: string; label: string; result?: MonthCellType | 'reel' } | null;
type ResolutionOf = (employeeId: string, cell: MonthCellDTO) => Resolution;

const STAGE_INDEX: Record<MonthlyStage, number> = {
  DRAFT: 0,
  PREPARED_BY_LAMIS: 1,
  REVIEWED_BY_BOUCHRA: 2,
  VALIDATED_BY_CHOUAIB: 3,
};
const STEPS = [
  { who: 'Lamis', role: 'Préparation' },
  { who: 'Bouchra', role: 'Validation' },
  { who: 'Chouaib', role: 'Approbation' },
];

function nextTransition(stage: MonthlyStage, role: string | null): { stage: MonthlyStage; label: string; allowed: boolean } | null {
  if (stage === 'DRAFT') return { stage: 'PREPARED_BY_LAMIS', label: 'Soumettre à Bouchra', allowed: true };
  if (stage === 'PREPARED_BY_LAMIS') return { stage: 'REVIEWED_BY_BOUCHRA', label: 'Soumettre à Chouaib', allowed: role === 'ADMIN' || role === 'BOSS' };
  if (stage === 'REVIEWED_BY_BOUCHRA') return { stage: 'VALIDATED_BY_CHOUAIB', label: 'Valider la grille', allowed: role === 'BOSS' };
  return null;
}

const SUBMITTED_TITLES: Record<MonthlyStage, string> = {
  DRAFT: '',
  PREPARED_BY_LAMIS: 'Transmis à Bouchra',
  REVIEWED_BY_BOUCHRA: 'Transmis à Chouaib',
  VALIDATED_BY_CHOUAIB: 'Grille validée',
};

export function MonthlyGridPage() {
  const me = useMe();
  const qc = useQueryClient();
  const entityId = useUiStore((s) => s.currentEntityId);
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();
  const year = Number(searchParams.get('year')) || now.getFullYear();
  const month = Number(searchParams.get('month')) || now.getMonth() + 1;
  const inReview = searchParams.get('view') === 'review';

  const [version, setVersion] = useState<Version>('real');
  const [overlay, setOverlay] = useState<Overlay>({});
  const [reviewStart, setReviewStart] = useState(0);
  const [submittedTo, setSubmittedTo] = useState<MonthlyStage | null>(null);
  const [confirmMissing, setConfirmMissing] = useState<string[] | null>(null);

  const grid = useMonthlyGrid({ entityId, year, month });
  const submit = useSubmitMonthly();
  const decide = usePaieDecision();

  // « Absence à requalifier » is the same call as the daily arbitrage, so
  // it offers the same catalogue types (Paramètres → Types de décision).
  const { data: decisionTypes } = useDecisionTypes();
  const customTypes = useMemo<GmCustomType[]>(
    () => (decisionTypes ?? []).filter((t) => !t.builtin).map((t) => ({ code: t.code, label: t.label })),
    [decisionTypes],
  );

  const scopeKey = `${entityId}|${year}|${month}`;
  useEffect(() => {
    setOverlay({});
    setSubmittedTo(null);
    setConfirmMissing(null);
  }, [scopeKey]);

  const setMonth = (y: number, m: number) => {
    const p = new URLSearchParams(searchParams);
    p.set('year', String(y));
    p.set('month', String(m));
    p.delete('view');
    setSearchParams(p, { replace: true });
  };
  const setView = (view: 'grid' | 'review') => {
    const p = new URLSearchParams(searchParams);
    if (view === 'review') p.set('view', 'review');
    else p.delete('view');
    setSearchParams(p, { replace: true });
  };

  const data = grid.data;
  const role = me.data?.user.role ?? null;
  const stage = data?.submission.stage ?? 'DRAFT';
  const expectedHours = data?.expectedHours ?? 8;

  // ─── the decide queue (derived: every cell flagged by the server) ───
  const queue: QueueItem[] = useMemo(() => {
    if (!data) return [];
    const q: QueueItem[] = [];
    data.rows.forEach((row) => {
      row.cells.forEach((cell, dayIdx) => {
        if (!cell.needKind) return;
        q.push({
          employeeId: row.employee.id,
          name: row.employee.name,
          role: row.employee.role,
          date: cell.date,
          day: data.days[dayIdx]!.day,
          dayIdx,
          kind: cell.needKind,
          cell,
        });
      });
    });
    return q;
  }, [data]);

  // Resolution of a queue cell = local echo first, then server state.
  const resolutionOf = useCallback<ResolutionOf>(
    (employeeId, cell) => {
      const local = overlay[ovKey(employeeId, cell.date)];
      if (local !== undefined) {
        return local ? { key: local.key, label: local.label, result: local.result as MonthCellType | 'reel' } : null;
      }
      if (cell.paieDecision && cell.typePaie !== 'need') {
        return { key: cell.paieDecision.key, label: cell.paieDecision.label, result: cell.typePaie };
      }
      return null;
    },
    [overlay],
  );

  const decidesLeft = useMemo(
    () => queue.filter((q) => !resolutionOf(q.employeeId, q.cell)).length,
    [queue, resolutionOf],
  );
  const ready = decidesLeft === 0;

  const openReview = (start = 0) => {
    setReviewStart(start);
    setVersion('paie');
    setView('review');
  };
  const pickCell = (employeeId: string, date: string) => {
    const i = queue.findIndex((q) => q.employeeId === employeeId && q.date === date);
    openReview(i < 0 ? 0 : i);
  };

  const commitDecision = async (item: QueueItem, opt: GmDecisionOption) => {
    setOverlay((o) => ({ ...o, [ovKey(item.employeeId, item.date)]: opt }));
    try {
      await decide.mutateAsync({
        entityId, year, month, employeeId: item.employeeId, date: item.date, key: opt.key,
      });
    } catch (err) {
      setOverlay((o) => {
        const next = { ...o };
        delete next[ovKey(item.employeeId, item.date)];
        return next;
      });
      const e = err as Error & { body?: { message?: string } };
      toast.danger(e.body?.message ?? 'Échec de l\'enregistrement de la décision.');
      throw err;
    }
  };

  const trans = nextTransition(stage, role);
  const doSubmit = async (force = false) => {
    if (!trans || !trans.allowed) return;
    try {
      await submit.mutateAsync({ entityId, year, month, stage: trans.stage, ...(force ? { force } : {}) });
      setSubmittedTo(trans.stage);
      toast.success('Étape mise à jour · email envoyé.');
    } catch (err) {
      const e = err as Error & { body?: { error?: string; message?: string; dates?: string[] } };
      // Journées sans pointage importé: on ne bloque pas définitivement,
      // on demande une confirmation explicite (le correctif normal est
      // d'importer le fichier, pas d'arbitrer cellule par cellule).
      if (e.body?.error === 'pointage_missing' && e.body.dates?.length) {
        setConfirmMissing(e.body.dates);
        return;
      }
      toast.danger(e.body?.message ?? 'Échec de la soumission.');
    }
  };

  // Champ ajouté après coup: une réponse qui ne le porte pas (cache,
  // API plus ancienne) ne doit pas faire tomber toute la page.
  const missingDates = data?.missingPointageDates ?? [];

  const pdfHref = `/api/monthly-grid/pdf?entityId=${entityId}&year=${year}&month=${month}&version=${version === 'paie' ? 'paie' : 'real'}`;
  const monthLabel = `${MONTHS[month - 1]} ${year}`;

  const shell = (children: React.ReactNode) => (
    <div className={'gmapp' + (themeDark ? ' theme-dark' : '')}>
      <div className="gm-scrollpage">
        <div className="gm-wrap">{children}</div>
      </div>
    </div>
  );

  if (entityId === 'all') {
    return shell(
      <>
        <div className="gm-eyebrow">{monthLabel}</div>
        <h1 className="gm-title">Grille mensuelle</h1>
        <p className="gm-placeholder">Sélectionnez une entité (barre du haut) pour afficher la grille mensuelle.</p>
      </>,
    );
  }
  if (grid.isLoading) {
    return shell(
      <>
        <div className="gm-eyebrow">{monthLabel}</div>
        <h1 className="gm-title">Grille mensuelle</h1>
        <p className="gm-placeholder">Chargement…</p>
      </>,
    );
  }
  if (grid.isError || !data) {
    return shell(
      <>
        <div className="gm-eyebrow">{monthLabel}</div>
        <h1 className="gm-title">Grille mensuelle</h1>
        <p className="gm-placeholder" data-testid="monthly-error">Impossible de charger la grille.</p>
      </>,
    );
  }

  // ─── VIEW 3 — submitted ───
  if (submittedTo) {
    return shell(
      <div className="view-enter" key="sb">
        <div className="sb">
          <div className="sb-badge">✓</div>
          <div className="sb-eyebrow">{monthLabel} · {data.entity.name}</div>
          <h1>{SUBMITTED_TITLES[submittedTo]}</h1>
          <p>
            La version paie est verrouillée et envoyée pour validation.<br />
            {data.rows.length} employé{data.rows.length > 1 ? 's' : ''} · {queue.length} décision{queue.length > 1 ? 's' : ''} arbitrée{queue.length > 1 ? 's' : ''} · 0 en attente.
          </p>
          <div className="sb-chain"><Stepper step={STAGE_INDEX[submittedTo]} /></div>
          <div className="sb-actions">
            <button className="sb-btn primary" onClick={() => setSubmittedTo(null)} data-testid="submitted-back">
              ← Revoir la grille
            </button>
          </div>
        </div>
      </div>,
    );
  }

  // ─── VIEW 2 — review overlay ───
  if (inReview && queue.length > 0) {
    return shell(
      <div className="view-enter" key="review">
        <ReviewView
          data={data}
          queue={queue}
          startIndex={reviewStart}
          monthLabel={monthLabel}
          expectedHours={expectedHours}
          customTypes={customTypes}
          resolutionOf={resolutionOf}
          onCommit={commitDecision}
          onPunchesFixed={() => qc.invalidateQueries({ queryKey: ['monthly-grid', entityId, year, month] })}
          onExit={() => setView('grid')}
        />
      </div>,
    );
  }

  // ─── VIEW 1 — grid ───
  return shell(
    <div className="view-enter" key="grid">
      <div className="gm-eyebrow">{monthLabel}</div>
      <h1 className="gm-title">Grille mensuelle <em>{data.entity.name}</em></h1>
      <p className="gm-sub">Vue d’ensemble jour par jour. Bascule réelle / paie, soumission en 3 étapes.</p>

      <div className="gm-ctrl">
        <div className="gm-monthnav">
          <button className="gm-mn-arrow" onClick={() => setMonth(month === 1 ? year - 1 : year, month === 1 ? 12 : month - 1)} aria-label="Mois précédent">
            <ChevronLeft size={15} aria-hidden="true" />
          </button>
          <span className="gm-mn-lbl">📅 {monthLabel}</span>
          <button className="gm-mn-arrow" onClick={() => setMonth(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1)} aria-label="Mois suivant">
            <ChevronRight size={15} aria-hidden="true" />
          </button>
          <button className="gm-mn-today" onClick={() => setMonth(now.getFullYear(), now.getMonth() + 1)}>Ce mois-ci</button>
        </div>

        <VersionToggle version={version} onChange={setVersion} />
        <Stepper step={STAGE_INDEX[stage]} submission={data.submission} />

        <div className="gm-ctrlspace" />
        <a className="gm-pdf" href={pdfHref} target="_blank" rel="noreferrer" data-testid="monthly-pdf">
          <Download size={14} aria-hidden="true" /> PDF entité
        </a>
        {trans && (
          ready ? (
            <button
              className="gm-submit ready"
              onClick={() => doSubmit()}
              disabled={!trans.allowed || submit.isPending}
              title={trans.allowed ? undefined : 'Réservé au relecteur de cette étape.'}
              data-testid="monthly-submit"
            >
              <Check size={15} aria-hidden="true" /> {trans.label}
            </button>
          ) : (
            <button className="gm-submit locked" onClick={() => openReview(0)} data-testid="monthly-submit">
              <Zap size={15} aria-hidden="true" /> Réviser {decidesLeft} décision{decidesLeft > 1 ? 's' : ''}
            </button>
          )
        )}
      </div>

      <Legend />

      {missingDates.length > 0 && (
        <div className="gm-attn noimport" data-testid="monthly-banner-noimport">
          <span className="gm-attn-ic">–</span>
          <span className="gm-attn-txt">
            <b>
              {missingDates.length} journée{missingDates.length > 1 ? 's' : ''} sans
              pointage importé
            </b>{' '}
            — {dayList(missingDates)}. Personne n'a badgé ces jours-là : ce sont des absences
            de <em>données</em>, pas de personnes. Importez le fichier de la pointeuse, ou déclarez la journée
            fériée dans Paramètres.
          </span>
        </div>
      )}

      {stage === 'DRAFT' && (
        ready ? (
          <div className="gm-attn done" data-testid="monthly-banner-done">
            <span className="gm-attn-ic">✓</span>
            <span className="gm-attn-txt"><b>Grille prête.</b> Toutes les décisions paie sont arbitrées — la version paie peut être soumise à Bouchra.</span>
          </div>
        ) : (
          <div className="gm-attn todo" data-testid="monthly-banner-todo">
            <span className="gm-attn-ic">?</span>
            <span className="gm-attn-txt"><b>{decidesLeft} cellule{decidesLeft > 1 ? 's' : ''} à décider</b> avant la soumission paie — journées courtes, absences à requalifier et anomalies de pointage.</span>
            <button className="gm-attn-go" onClick={() => openReview(0)} data-testid="monthly-banner-go">⚡ Réviser les décisions</button>
          </div>
        )
      )}

      <GridView
        data={data}
        version={version}
        expectedHours={expectedHours}
        resolutionOf={resolutionOf}
        onOpenReview={() => openReview(0)}
        onPickCell={pickCell}
      />

      {confirmMissing && (
        <div className="gm-veil" onClick={() => setConfirmMissing(null)}>
          <div className="gm-confirm" onClick={(e) => e.stopPropagation()} data-testid="monthly-confirm-noimport">
            <h3>Soumettre malgré le pointage manquant ?</h3>
            <p>
              {confirmMissing.length} journée{confirmMissing.length > 1 ? 's' : ''} n'
              {confirmMissing.length > 1 ? 'ont' : 'a'} aucun pointage importé : {dayList(confirmMissing)}. Ces
              journées partiront en paie à 0 h pour tout le monde.
            </p>
            <div className="gm-confirm-act">
              <button className="sb-btn" onClick={() => setConfirmMissing(null)}>Annuler</button>
              <button
                className="gm-attn-go"
                onClick={() => { setConfirmMissing(null); void doSubmit(true); }}
                data-testid="monthly-confirm-go"
              >
                Soumettre quand même
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
  );
}

// « 12, 13 et 14 août » — les dates manquantes en une ligne lisible.
function dayList(dates: string[]): string {
  const nums = dates.map((d) => String(Number(d.slice(8, 10))));
  const months = [...new Set(dates.map((d) => MONTHS[Number(d.slice(5, 7)) - 1]))];
  const suffix = months.length === 1 ? ` ${months[0]}` : '';
  if (nums.length === 1) return `${nums[0]}${suffix}`;
  const head = nums.slice(0, -1).join(', ');
  return `${head} et ${nums[nums.length - 1]}${suffix}`;
}

// ─── version toggle (animated thumb measured from the active button) ───
function VersionToggle({ version, onChange }: { version: Version; onChange: (v: Version) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState({ left: 3, width: 122 });
  const measure = useCallback(() => {
    if (!ref.current) return;
    const btns = ref.current.querySelectorAll('button');
    const el = version === 'real' ? btns[0] : btns[1];
    if (el && el.offsetWidth) setThumb({ left: el.offsetLeft, width: el.offsetWidth });
  }, [version]);
  useLayoutEffect(() => {
    measure();
    const raf = requestAnimationFrame(measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => undefined);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [measure]);
  return (
    <div className="gm-seg" ref={ref} role="tablist" aria-label="Version">
      <div className="gm-seg-thumb" style={{ left: thumb.left, width: thumb.width }} />
      <button className={version === 'real' ? 'on' : ''} onClick={() => onChange('real')} data-testid="version-real">
        <span className="dot" />Version réelle
      </button>
      <button className={version === 'paie' ? 'on' : ''} onClick={() => onChange('paie')} data-testid="version-paie">
        <span className="dot" />Version paie
      </button>
    </div>
  );
}

// ─── submission stepper ───
function Stepper({ step, submission }: { step: number; submission?: MonthGridDTO['submission'] }) {
  const titles = [
    submission?.preparedAt ? `Préparé le ${submission.preparedAt.slice(0, 10)}` : 'Préparation',
    submission?.reviewedAt ? `Revu le ${submission.reviewedAt.slice(0, 10)}` : 'Validation',
    submission?.validatedAt ? `Validé le ${submission.validatedAt.slice(0, 10)}` : 'Approbation',
  ];
  return (
    <div className="gm-stepper" data-testid="monthly-workflow" data-stage-index={step}>
      {STEPS.map((s, i) => (
        <span key={s.who} style={{ display: 'contents' }}>
          {i > 0 && <span className={'gm-step-line' + (i <= step ? ' done' : '')} />}
          <div className={'gm-step' + (i < step ? ' done' : i === step ? ' current' : '')} title={titles[i]}>
            <span className="gm-step-dot">{i < step ? '✓' : i + 1}</span>
            <span className="gm-step-meta">
              <span className="gm-step-who">{s.who}</span>
              <span className="gm-step-role">{s.role}</span>
            </span>
          </div>
        </span>
      ))}
    </div>
  );
}

// ─── legend (the cell-encoding key) ───
const LEGEND: Array<[string, string, React.CSSProperties]> = [
  ['Présent', 'présent', { background: 'var(--s3)' }],
  ['Retard / court', 'retard', { background: 'var(--warns)' }],
  ['Récup (R)', 'récup', { background: 'var(--warns)' }],
  ['Télétravail (TT)', 'tt', { background: 'var(--shfs)' }],
  ['Congé (C)', 'congé', { background: 'var(--shfs)' }],
  ['Maladie (M)', 'maladie', { background: 'var(--infos)' }],
  ['Justifiée (AJ)', 'justifiée', { background: 'var(--greens)' }],
  ['Non justif. (A)', 'non-justifiée', { background: 'var(--bad)' }],
  // À décider (paie) — la couleur du « ? » dit la raison de l'arbitrage.
  ['? Absence à requalifier', 'decider-absj', { background: 'var(--card)', border: '1.5px dashed var(--bad)' }],
  ['? Retard / journée courte', 'decider-court', { background: 'var(--card)', border: '1.5px dashed var(--warn)' }],
  ['? Anomalie de pointage', 'decider-double', { background: 'var(--card)', border: '1.5px dashed var(--shf)' }],
  ['– Pointage non importé', 'non-importe', { background: 'var(--s2)', border: '1.5px dotted var(--ink-3)' }],
  // Réelle : journée déjà arbitrée — relevé brut + pastille de décision.
  ['✓ Décidé — voir version paie', 'decide-fait', { background: 'var(--s2)', boxShadow: 'inset 0 0 0 1.5px var(--green)' }],
];

function Legend() {
  return (
    <div className="gm-legend">
      {LEGEND.map(([label, key, style]) => (
        <span className="gm-leg" key={key}>
          <span className="sw" style={style} />
          {label}
        </span>
      ))}
    </div>
  );
}

// ─── the grid ───
interface GridProps {
  data: MonthGridDTO;
  version: Version;
  expectedHours: number;
  resolutionOf: ResolutionOf;
  onOpenReview: () => void;
  onPickCell: (employeeId: string, date: string) => void;
}

function GridView({ data, version, expectedHours, resolutionOf, onOpenReview, onPickCell }: GridProps) {
  const [hover, setHover] = useState<{ row: MonthGridRowDTO; cell: MonthCellDTO; dayIdx: number; rect: DOMRect } | null>(null);

  const cellView = (employeeId: string, cell: MonthCellDTO): CellView => {
    // Queued cells show the pulsing ? only while undecided. Once
    // arbitrated, paie shows the decision; réelle keeps the raw punch
    // reading (decisions never rewrite the réelle) with a corner tag
    // naming the call. Réelle totals keep the true hours either way.
    if (cell.needKind) {
      const res = resolutionOf(employeeId, cell);
      if (version === 'real' && res?.result) {
        const tag = decisionTag(res.result);
        if (cell.needKind === 'court' && cell.hoursReal != null) {
          return { tone: 'late', text: fr(cell.hoursReal), decided: true, tag };
        }
        if (cell.needKind === 'double' && (cell.checkIn || cell.checkOut)) {
          const text = cell.checkIn ? `${cell.checkIn}→` : `→${cell.checkOut}`;
          return { tone: 'raw', text, decided: true, tag };
        }
        // absj — nothing punched. A neutral dash, never the red « A »:
        // the day was requalified, the tag says how.
        return { tone: 'raw', text: '—', decided: true, tag };
      }
      if (version === 'paie' && res?.result) {
        const t = res.result;
        // « Compter les heures réelles »: la seule décision qui ne pose
        // pas la journée standard — elle affiche les heures pointées.
        if (t === 'reel') {
          return { tone: 'late', text: cell.hoursReal != null ? fr(cell.hoursReal) : '', resolved: true };
        }
        const text = t === 'p' || t === 'tt' ? frTotal(expectedHours) : CODE[t] ?? '';
        return { tone: TONE[t], text, resolved: true };
      }
      return { tone: 'decide', text: '?', decide: true, kind: cell.needKind ?? undefined };
    }
    if (version === 'real') {
      const t = cell.typeReal;
      if (t === 'we' || t === 'empty') return { tone: 'off', text: '' };
      if (t === 'p' || t === 'r') {
        if (cell.hoursReal == null) return { tone: 'late', text: '?' };
        const over = t === 'p' && cell.hoursReal > expectedHours;
        return { tone: over ? 'over' : TONE[t], text: fr(cell.hoursReal) };
      }
      return { tone: TONE[t], text: CODE[t] ?? '' };
    }
    // paie version — over/retard days read as capped present hours
    const t = cell.typePaie;
    if (t === 'we' || t === 'empty') return { tone: 'off', text: '' };
    if (t === 'p' || t === 'r') {
      return { tone: 'pres', text: cell.hoursPaie != null ? frTotal(cell.hoursPaie) : '' };
    }
    if (t === 'tt') return { tone: 'tt', text: cell.hoursPaie != null ? frTotal(cell.hoursPaie) : 'TT' };
    return { tone: TONE[t], text: CODE[t] ?? '' };
  };

  // Row total under the active version — paie skips unresolved cells but
  // counts local echoes at the expected cap.
  const rowTotal = (row: MonthGridRowDTO): number => {
    if (version === 'real') return row.totals.hoursReal;
    let h = row.totals.hoursPaie;
    row.cells.forEach((cell) => {
      if (cell.typePaie !== 'need') return;
      const res = resolutionOf(row.employee.id, cell);
      if (!res?.result) return;
      // L'écho local suit la même règle que le serveur: heures réelles
      // pour « Compter les heures réelles », journée standard sinon.
      h += res.result === 'reel' ? cell.hoursReal ?? 0 : expectedHours;
    });
    return Math.round(h * 10) / 10;
  };
  const rowLeft = (row: MonthGridRowDTO): number =>
    row.cells.filter((c) => c.typePaie === 'need' && !resolutionOf(row.employee.id, c)).length;

  // Écart d'heures du mois vs l'attendu, jours RÉELLEMENT travaillés
  // uniquement — TT, congés, absences, maladie, fériés et week-ends sont
  // exclus des deux côtés (même règle que l'Inspection). Toujours calculé
  // sur les heures réelles, quelle que soit la version affichée.
  const rowDelta = (row: MonthGridRowDTO): number => {
    let d = 0;
    for (const c of row.cells) {
      if ((c.typeReal === 'p' || c.typeReal === 'r') && c.hoursReal != null && !c.isWeekend && !c.isFerie) {
        d += c.hoursReal - expectedHours;
      }
    }
    return Math.round(d * 10) / 10;
  };
  const fmtDelta = (d: number): string => {
    const totalMin = Math.round(Math.abs(d) * 60);
    const H = Math.floor(totalMin / 60);
    const M = totalMin % 60;
    const body = H > 0 ? `${H}h${M ? String(M).padStart(2, '0') : ''}` : `${M}min`;
    return (d >= 0 ? '+' : '−') + body;
  };

  const dayTotal = (dayIdx: number) => {
    let h = 0;
    let present = 0;
    let workers = 0;
    data.rows.forEach((row) => {
      const cell = row.cells[dayIdx]!;
      const t = version === 'real' ? cell.typeReal : cell.typePaie;
      // 'na' = journée non importée: elle ne dit rien de la présence, donc
      // elle ne doit pas écraser le taux du jour à 0 %.
      if (t === 'we' || t === 'h' || t === 'empty' || t === 'na') return;
      workers++;
      let hrs = version === 'real' ? cell.hoursReal : cell.hoursPaie;
      if (version === 'paie' && cell.typePaie === 'need') {
        const res = resolutionOf(row.employee.id, cell);
        if (res?.result) hrs = expectedHours;
      }
      if (hrs && hrs > 0) { h += hrs; present++; }
      else if (t === 'tt') present++;
    });
    return { h, present, workers };
  };

  const grandTotal = data.rows.reduce((a, r) => a + rowTotal(r), 0);

  return (
    <div className="gm-gridwrap d-cozy">
      <div className="gm-scroll" data-testid="monthly-grid-scroll">
        <table className="gm-tbl">
          <thead>
            <tr className="gm-headrow">
              <th className="gm-cEmp gm-stickL">
                <span className="gm-th-emp">Employé</span>
                <span className="gm-th-count gm-mono">{data.rows.length}</span>
              </th>
              {data.days.map((d) => (
                <th
                  key={d.date}
                  className={'gm-cDay' + (d.isWeekend ? ' off' : '') + (d.pointageMissing ? ' noimport' : '')}
                  title={d.pointageMissing ? 'Pointage non importé pour cette journée' : undefined}
                >
                  <span className="gm-dn">{d.day}</span>
                  <span className="gm-dw">{DOW[d.dow]}</span>
                </th>
              ))}
              <th className="gm-cTot gm-stickR">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const left = rowLeft(row);
              return (
                <tr key={row.employee.id} className="gm-row">
                  <td className="gm-cEmp gm-stickL">
                    <div className="gm-emp">
                      <span className="gm-av" style={{ background: 'var(--shf)' }}>{gmInitials(row.employee.name)}</span>
                      <div className="gm-empmeta">
                        <span className="gm-name">{row.employee.name}</span>
                        {row.employee.role && <span className="gm-role">{row.employee.role}</span>}
                      </div>
                    </div>
                  </td>
                  {row.cells.map((cell, i) => {
                    const cv = cellView(row.employee.id, cell);
                    const day = data.days[i]!;
                    return (
                      <td key={cell.date} className={'gm-td' + (day.isWeekend ? ' off' : '')}>
                        {cv.tone === 'off'
                          ? <span className="gm-cell off" />
                          : (
                            <span
                              className={'gm-cell t-' + cv.tone + (cv.kind ? ' k-' + cv.kind : '') + (cv.resolved ? ' is-res' : '') + (cv.decided ? ' is-dec' : '')}
                              onClick={cv.decide || cv.decided ? () => onPickCell(row.employee.id, cell.date) : undefined}
                              onMouseEnter={(e) => setHover({ row, cell, dayIdx: i, rect: e.currentTarget.getBoundingClientRect() })}
                              onMouseLeave={() => setHover(null)}
                              data-testid={cv.decide ? `need-${row.employee.id}-${cell.date}` : cv.decided ? `decided-${row.employee.id}-${cell.date}` : undefined}
                            >{cv.text}{cv.tag && <i className={'gm-dtag t-' + cv.tag.tone}>{cv.tag.text}</i>}</span>
                          )}
                      </td>
                    );
                  })}
                  <td className="gm-cTot gm-stickR">
                    <div className="gm-totcell">
                      <div className="gm-totstack">
                        <span className="gm-toth">{frTotal(rowTotal(row))}</span>
                        {(() => {
                          const d = rowDelta(row);
                          const cls = d > 0.05 ? 'pos' : d < -0.05 ? 'neg' : 'zero';
                          return (
                            <span
                              className={'gm-totdelta ' + cls}
                              title="Écart vs attendu sur les jours réellement travaillés — TT, congés, absences, fériés et week-ends exclus"
                              data-testid={`totdelta-${row.employee.id}`}
                            >
                              {cls === 'zero' ? '±0' : fmtDelta(d)}
                            </span>
                          );
                        })()}
                      </div>
                      {left > 0 && (
                        <span className="gm-totbadge" onClick={onOpenReview} data-testid={`totbadge-${row.employee.id}`}>{left} ?</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="gm-footrow">
              <td className="gm-cEmp gm-stickL"><span className="gm-foot-lbl">Présence / jour</span></td>
              {data.days.map((d, i) => {
                if (d.isWeekend) return <td key={d.date} className="gm-cDay off" />;
                const dt = dayTotal(i);
                const rate = dt.workers ? dt.present / dt.workers : 0;
                return (
                  <td key={d.date} className="gm-footcell">
                    <span className="gm-footh">{dt.h ? Math.round(dt.h) : '·'}</span>
                    <span className="gm-footbar"><i style={{ height: `${8 + rate * 18}px`, opacity: 0.25 + rate * 0.6 }} /></span>
                  </td>
                );
              })}
              <td className="gm-cTot gm-stickR">
                <span className="gm-grandtot" data-testid="monthly-grandtot">{frTotal(grandTotal)}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {hover && (
        <CellPopover
          row={hover.row}
          cell={hover.cell}
          day={data.days[hover.dayIdx]!}
          rect={hover.rect}
          version={version}
          month={data.month}
          resolutionOf={resolutionOf}
          cellView={cellView}
        />
      )}
    </div>
  );
}

// ─── hover popover ───
function CellPopover({ row, cell, day, rect, version, month, resolutionOf, cellView }: {
  row: MonthGridRowDTO;
  cell: MonthCellDTO;
  day: MonthGridDTO['days'][number];
  rect: DOMRect;
  version: Version;
  month: number;
  resolutionOf: ResolutionOf;
  cellView: (employeeId: string, cell: MonthCellDTO) => CellView;
}) {
  const cv = cellView(row.employee.id, cell);
  if (cv.tone === 'off') return null;
  const res = resolutionOf(row.employee.id, cell);
  const isDecide = Boolean(cv.decide);

  const style: React.CSSProperties = {
    left: Math.min(Math.max(rect.left + rect.width / 2, 130), window.innerWidth - 130),
    top: rect.bottom + 10,
  };

  const TAG_LABEL: Partial<Record<MonthCellType, string>> = {
    p: 'Présent', r: 'Retard', recup: 'Récupération', tt: 'Télétravail', c: 'Congé',
    m: 'Maladie', aj: 'Absence justifiée', anj: 'Non justifié', h: 'Férié',
    na: 'Pointage non importé',
  };

  let title: string;
  let tagTone: string;
  const lines: Array<[string, string]> = [];
  if (isDecide) {
    title = 'À décider · paie';
    tagTone = 'decide';
    if (cell.checkIn || cell.checkOut) {
      lines.push([
        `${cell.checkIn ?? '—'} → ${cell.checkOut ?? '—'}`,
        cell.hoursReal != null ? `${fr(cell.hoursReal)} pointées` : 'badge incohérent',
      ]);
    } else {
      lines.push(['Aucun pointage', '']);
    }
  } else if (cv.decided && res) {
    // Réelle, arbitrated: the raw reading first, then the paie call.
    title = 'Décidé · paie';
    tagTone = cv.tag?.tone ?? 'pres';
    if (cell.checkIn || cell.checkOut) {
      lines.push([
        `${cell.checkIn ?? '—'} → ${cell.checkOut ?? '—'}`,
        cell.hoursReal != null ? `${fr(cell.hoursReal)} pointées` : 'badge incohérent',
      ]);
    } else {
      lines.push(['Aucun pointage', '']);
    }
    lines.push(['Décision paie', res.label]);
  } else if (version === 'paie' && res?.result) {
    title = res.label;
    tagTone = res.result === 'p' ? 'pres' : res.result === 'reel' ? 'late' : TONE[res.result] ?? 'pres';
    lines.push(['Décision paie enregistrée', '']);
  } else {
    const t = version === 'real' ? cell.typeReal : cell.typePaie;
    title = TAG_LABEL[t] ?? '';
    tagTone = cv.tone === 'over' ? 'over' : cv.tone;
    if (cell.checkIn && cell.checkOut && cell.hoursReal != null) {
      lines.push([`${cell.checkIn} → ${cell.checkOut}`, fr(cell.hoursReal)]);
    } else if (t === 'na') {
      lines.push(['Aucun pointage importé ce jour-là', '']);
      lines.push(['Ni présence ni absence constatée', '']);
    } else {
      lines.push([title, '']);
    }
  }

  return (
    <div className="gm-pop" style={style}>
      <div className="gm-pop-arrow" />
      <div className="gm-pop-head">
        <span className="gm-pop-name">{row.employee.name.split(/\s+/).slice(0, 2).join(' ')}</span>
        <span className={'gm-pop-tag t-' + tagTone}>{title}</span>
      </div>
      <div className="gm-pop-date">{DOW_NAME[day.dow]} {day.day} {MONTHS[month - 1]}</div>
      {lines.map((l, i) => (
        <div className="gm-pop-line" key={i}><span>{l[0]}</span>{l[1] && <b>{l[1]}</b>}</div>
      ))}
      {isDecide && <div className="gm-pop-cta">Cliquer pour arbitrer →</div>}
      {cv.decided && <div className="gm-pop-cta">Cliquer pour modifier la décision →</div>}
    </div>
  );
}

// ─── review overlay (keyboard-first arbitration) ───
function ReviewView({ data, queue, startIndex, monthLabel, expectedHours, customTypes, resolutionOf, onCommit, onPunchesFixed, onExit }: {
  data: MonthGridDTO;
  queue: QueueItem[];
  startIndex: number;
  monthLabel: string;
  expectedHours: number;
  customTypes: GmCustomType[];
  resolutionOf: ResolutionOf;
  onCommit: (item: QueueItem, opt: GmDecisionOption) => Promise<void>;
  onPunchesFixed: () => void;
  onExit: () => void;
}) {
  const total = queue.length;
  const [idx, setIdx] = useState(Math.min(startIndex, total - 1));
  const item = queue[Math.min(idx, total - 1)];
  const [committed, setCommitted] = useState(false);
  const [saisieOpen, setSaisieOpen] = useState(false);

  const menu = item ? gmMenuFor(item.kind, customTypes) : null;
  const existing = item ? resolutionOf(item.employeeId, item.cell) : null;
  const [pick, setPick] = useState(existing?.key ?? menu?.options[0]?.key ?? '');

  const resolvedCount = queue.filter((q) => resolutionOf(q.employeeId, q.cell)).length;

  useEffect(() => {
    if (!item || !menu) return;
    const ex = resolutionOf(item.employeeId, item.cell);
    setPick(ex?.key ?? menu.options[0]!.key);
    setCommitted(false);
    setSaisieOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, item?.employeeId, item?.date]);

  const nextUnresolved = useCallback((skipKey?: string) => {
    for (let s = 1; s <= total; s++) {
      const j = (idx + s) % total;
      const q = queue[j]!;
      const k = ovKey(q.employeeId, q.date);
      if (k === skipKey) continue;
      if (!resolutionOf(q.employeeId, q.cell)) return j;
    }
    return -1;
  }, [idx, total, queue, resolutionOf]);

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (advanceTimer.current) clearTimeout(advanceTimer.current); }, []);

  const commit = useCallback(() => {
    if (!item || !menu || committed) return;
    const opt = menu.options.find((o) => o.key === pick);
    if (!opt) return;
    if (opt.manual) {
      setSaisieOpen(true);
      return;
    }
    setCommitted(true);
    onCommit(item, opt)
      .then(() => {
        advanceTimer.current = setTimeout(() => {
          const j = nextUnresolved(ovKey(item.employeeId, item.date));
          if (j === -1) onExit();
          else setIdx(j);
        }, 430);
      })
      .catch(() => setCommitted(false));
  }, [item, menu, committed, pick, onCommit, nextUnresolved, onExit]);

  const skip = useCallback(() => {
    const j = nextUnresolved();
    if (j !== -1) setIdx(j);
  }, [nextUnresolved]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = ((e.target as HTMLElement)?.tagName ?? '').toLowerCase();
      if (tag === 'textarea' || tag === 'input' || tag === 'select') return;
      if (e.key === 'Escape') { onExit(); return; }
      if (e.key === 'Enter') { commit(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); skip(); return; }
      const opt = menu?.options.find((o) => o.key === e.key.toUpperCase());
      if (opt) setPick(opt.key);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [menu, commit, skip, onExit]);

  if (!item || !menu) return null;
  const cell = item.cell;
  const day = data.days[item.dayIdx]!;
  const doneNow = resolvedCount;

  // headline flag + 4-stat readout per kind
  const missing = Math.max(0, expectedHours - (cell.hoursReal ?? 0));
  const flag = item.kind === 'absj'
    ? { tone: 'abs', big: '0h', lbl: 'aucun pointage' }
    : item.kind === 'double'
      ? { tone: 'late', big: '— h', lbl: 'badge incohérent' }
      : { tone: 'late', big: cell.hoursReal != null ? fr(cell.hoursReal) : '—', lbl: `sur ${fr(expectedHours)} prévues` };
  const readout: Array<[string, string, string?]> = item.kind === 'absj'
    ? [['—', 'entrée'], ['—', 'sortie'], ['0h', 'pointées'], [frTotal(expectedHours), 'manquantes', 'hot']]
    : item.kind === 'double'
      ? [[cell.checkIn ?? '—', 'badge 1'], [cell.checkOut ?? '—', 'badge 2'], ['?', 'cohérence', 'hot'], [frTotal(expectedHours), 'à reconstituer']]
      : [[cell.checkIn ?? '—', 'entrée'], [cell.checkOut ?? '—', 'sortie'], [cell.hoursReal != null ? fr(cell.hoursReal) : '—', 'pointées'], [`−${fr(missing)}`, 'manquantes', 'hot']];

  return (
    <div className="gr-root">
      <div className="gr-top">
        <button className="gr-back" onClick={onExit}>← Retour à la grille</button>
        <div className="gr-mode">⚡ Révision paie</div>
        <div className="gr-prog">
          <span className="gr-prog-lbl">Décision</span>
          <span className="gr-prog-n" data-testid="review-progress">{doneNow}<i>/{total}</i></span>
          <div className="gr-dots">
            {queue.map((q, i) => (
              <span key={ovKey(q.employeeId, q.date)} className={'gr-dot' + (resolutionOf(q.employeeId, q.cell) ? ' done' : '') + (i === idx ? ' on' : '')} />
            ))}
          </div>
        </div>
        <div className="gr-topspace" />
        <div className="gr-date">{monthLabel} · {data.entity.name}</div>
        <button className="gr-exit" onClick={onExit} data-testid="review-exit">Quitter <kbd>Esc</kbd></button>
      </div>
      <div className="gr-progbar"><span style={{ width: `${(doneNow / total) * 100}%` }} /></div>

      <div className="gr-stage">
        <div className="gr-rail">
          <div className="gr-rail-h">File · paie</div>
          {queue.map((q, i) => {
            const done = Boolean(resolutionOf(q.employeeId, q.cell));
            return (
              <button
                key={ovKey(q.employeeId, q.date)}
                className={'gr-railitem' + (i === idx ? ' on' : '') + (done ? ' done' : '')}
                onClick={() => setIdx(i)}
                data-testid={`rail-${q.employeeId}-${q.date}`}
              >
                <span className="gr-av" style={{ background: 'var(--shf)' }}>{done ? '✓' : gmInitials(q.name)}</span>
                <div className="gr-railmeta">
                  <span className="gr-railname">{q.name.split(/\s+/)[0]}</span>
                  <span className={'gr-railtag' + (done ? ' done' : '')}>
                    {done ? resolutionOf(q.employeeId, q.cell)!.label : GM_KIND_MENUS[q.kind].title}
                  </span>
                </div>
                <span className="gr-railday">{q.day}/{data.month}</span>
              </button>
            );
          })}
          <div className="gr-railnote">{total - doneNow} restante(s)</div>
        </div>

        <div className="gr-card" key={ovKey(item.employeeId, item.date)}>
          {committed && <div className="gr-confirm" data-testid="review-confirm"><span>✓</span> Décision enregistrée</div>}
          <div className="gr-card-head">
            <span className="gr-av big" style={{ background: 'var(--shf)' }}>{gmInitials(item.name)}</span>
            <div className="gr-id">
              <h1 className="gr-name">{item.name}</h1>
              <div className="gr-role">
                {item.role ?? '—'} · <b>{DOW_NAME[day.dow]} {day.day} {MONTHS[data.month - 1]}</b> · <span className="gr-kind">{menu.title}</span>
              </div>
            </div>
            <div className={'gr-flag t-' + flag.tone}>
              <span className="gr-flag-big">{flag.big}</span>
              <span className="gr-flag-lbl">{flag.lbl}</span>
            </div>
          </div>

          <div className="gr-readout">
            {readout.map((ro, i) => (
              <div key={i} className={'gr-ro' + (ro[2] === 'hot' ? ' hot' : '')}>
                <span className="gr-ro-v">{ro[0]}</span>
                <span className="gr-ro-l">{ro[1]}</span>
              </div>
            ))}
          </div>

          <div className="gr-decide-h">
            {item.kind === 'absj' ? <>Requalifier la journée&nbsp;?</> : <>Traitement paie&nbsp;?</>}
            {' '}<span>clique ou tape la touche</span>
          </div>
          <div className="gr-decgrid">
            {menu.options.map((o) => (
              <button
                key={o.key}
                className={'gr-dec t-' + o.tone + (pick === o.key ? ' hot' : '') + (o.customCode ? ' gr-dec-cust' : '')}
                onClick={() => { setPick(o.key); if (o.manual) setSaisieOpen(true); }}
                data-testid={`dec-${o.key}`}
              >
                {/* Catalogue types carry no shortcut — their code is the
                    stored key, not a keystroke. */}
                {o.customCode ? null : <kbd>{o.key}</kbd>}
                <span className="gr-dec-t">{o.label}</span>
                <span className="gr-dec-d">
                  {/* « Compter les heures réelles » annonce le chiffre
                      qu'elle va poser: c'est la seule option qui compte
                      la journée sous les heures attendues. */}
                  {o.result === 'reel' && cell.hoursReal != null
                    ? `${fr(cell.hoursReal)} comptabilisées telles quelles`
                    : o.desc}
                </span>
              </button>
            ))}
          </div>

          {saisieOpen && (
            <SaisieHoraire
              item={item}
              onSaved={() => {
                onPunchesFixed();
                setSaisieOpen(false);
                const j = nextUnresolved(ovKey(item.employeeId, item.date));
                if (j === -1) onExit();
                else setIdx(j); // queue shrinks on refetch; idx re-clamps via Math.min
              }}
              onCancel={() => setSaisieOpen(false)}
            />
          )}

          <div className="gr-foot">
            {/* Deux natures, deux phrases: l'équipe lisait « paie
                uniquement » sur une requalification d'absence et ne
                comprenait pas pourquoi la saisie quotidienne continuait
                de réclamer une décision. */}
            <div className="gr-foot-hint" data-testid="review-scope-hint">
              {item.kind === 'absj' ? (
                <>Cette décision s’écrit sur <b>la journée elle-même</b> — saisie quotidienne et grille (réelle comme paie) diront la même chose.</>
              ) : (
                <>Ajustement <b>paie uniquement</b> — la version réelle garde les heures brutes du pointage.</>
              )}
            </div>
            <div className="gr-topspace" />
            <button className="gr-skip" onClick={skip} data-testid="review-skip">Ignorer <kbd>↓</kbd></button>
            <button className="gr-next" onClick={commit} disabled={committed} data-testid="review-commit">
              Enregistrer &amp; suivant <kbd>↵</kbd>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// « Saisir l'horaire » — fixes the punches (réelle data) via the existing
// manual-punch endpoint; the cell then recomputes out of the queue.
function SaisieHoraire({ item, onSaved, onCancel }: { item: QueueItem; onSaved: () => void; onCancel: () => void }) {
  const put = usePutAttendance();
  const [checkIn, setCheckIn] = useState(item.cell.checkIn ?? '');
  const [checkOut, setCheckOut] = useState(item.cell.checkOut ?? '');
  const doSave = async () => {
    await put.mutateAsync({ date: item.date, employeeId: item.employeeId, update: { checkIn, checkOut } });
    toast.success('Pointage corrigé — la cellule est recalculée.');
    onSaved();
  };
  const save = async () => {
    if (!checkIn || !checkOut) {
      toast.danger('Entrée et sortie sont requises.');
      return;
    }
    try {
      await doSave();
    } catch (err) {
      const e = err as Error & { body?: { message?: string } };
      toast.danger(e.body?.message ?? 'Échec de la correction du pointage.');
    }
  };
  useUnsavedGuard({
    id: 'monthly-saisie-horaire',
    dirty: checkIn !== (item.cell.checkIn ?? '') || checkOut !== (item.cell.checkOut ?? ''),
    label: 'Grille mensuelle · saisie horaire',
    summary: `${item.name} · ${item.date}`,
    save: checkIn && checkOut ? doSave : undefined,
    discard: onCancel,
  });
  return (
    <div className="gr-saisie" data-testid="saisie-form">
      <label>
        Entrée
        <input type="time" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="saisie-in" />
      </label>
      <label>
        Sortie
        <input type="time" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="saisie-out" />
      </label>
      <div className="gr-topspace" />
      <button className="gr-saisie-cancel" onClick={onCancel}>Annuler</button>
      <button className="gr-saisie-save" onClick={save} disabled={put.isPending} data-testid="saisie-save">
        Corriger le pointage
      </button>
    </div>
  );
}
